/**
 * LX-02 Formulaic constructions (INV-CH-LX-02; changes C-D1-LX02, C-D2-LX02).
 *
 * Events: PHRASES hits (98 template phrases, one Aho-Corasick automaton; a phrase's own hits never
 * overlap, different phrases may) per 1k prose words. The seed's raw-count ramp `ramp(n, …)` is
 * removed (D1: it made the score depend on length and gave 30 at zero events, D2); the rate ramp
 * is the seed's, verbatim.
 */
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import {
	Tally,
	compiledEnglish,
	line,
	nativeMeasure,
	proseBlocks,
	quote,
	rateDef,
	wordExposure,
} from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

export const lx02Def = rateDef({
	id: 'LX-02',
	name: 'Formulaic constructions',
	family: 'FORMULA',
	features: [{ id: 'phrase', label: 'Template phrase' }],
	oneWay: true,
	minExposure: 300,
	exposureUnit: 'words',
	lang: 'native',
	ramp: [
		[0, 5],
		[0.8, 30],
		[2, 58],
		[4, 80],
		[8, 96],
	],
	unitScale: 1000,
	unitLabel: 'template phrases per 1k words',
	phi0: 1.5,
	weight: 1.8,
	nd: 0.5,
	changes: ['C-D1-LX02', 'C-D2-LX02'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const lex = compiledEnglish();
	const phrases = loadEnglishLexicons().phrases.entries;
	const limit = lex.phraseCount;
	const k = new Float64Array(doc.paragraphs.count);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	const tally = new Tally();
	for (const b of proseBlocks(doc)) {
		lex.phrases.matchAll(b.text, b.base, (s, en, idx) => {
			if (idx >= limit) return;
			k[b.p] = (k[b.p] as number) + 1;
			ev.add(s, en, 0, 1);
			tally.add(phrases[idx] as string);
		});
	}
	const summary = tally.top(14).map(([p, n]) => line(quote(p), `${n}×`));
	return { k, e, evidence: ev, summary };
}

export const lx02: ChannelImpl = rateChannel(lx02Def, nativeMeasure(measure));
export { lx02Def as def, lx02 as impl };
